import { useState, useEffect } from 'react';
import { X, Globe, Save, Trash2, AlertCircle, CheckCircle, Plus, Link, Eye, Key, Headphones } from 'lucide-react';
import { supabase } from '../../utils/supabaseStorage';
import { parseAdminStaffSession } from '../../utils/adminSession';
import { LoadingSpinner } from '../LoadingSpinner';

interface PageConfig {
  id?: string;
  route_path: string;
  modelconfig_id?: string | null;
  agent_template_id?: string | null;
  app_id: string;
  page_title?: string;
  page_description?: string;
  api_key_id?: string;
  hy_user_id?: string;
  example_media_url?: string;
  example_media_type?: string;
  created_at?: string;
  updated_at?: string;
}

interface PageSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  modelconfigId: string;
  modelName: string;
  /** 'agent' = public page for AI agent template (uses pages.agent_template_id) */
  variant?: 'model' | 'agent';
  agentTemplateId?: string;
}

interface ApiKey {
  id: string;
  provider: string;
  name: string;
  is_active: boolean;
}

/** Postgres uuid columns reject ''; use null for "cleared" optional FKs. */
function sanitizeUuidFields<T extends Record<string, unknown>>(row: T): T {
  const uuidKeys = ['api_key_id', 'modelconfig_id', 'agent_template_id'] as const;
  const out = { ...row } as Record<string, unknown>;
  for (const key of uuidKeys) {
    if (key in out && typeof out[key] === 'string' && out[key].trim() === '') {
      out[key] = null;
    }
  }
  return out as T;
}

export const PageSettingsModal = ({
  isOpen,
  onClose,
  modelconfigId,
  modelName,
  variant = 'model',
  agentTemplateId,
}: PageSettingsModalProps) => {
  const [existingPages, setExistingPages] = useState<PageConfig[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [mediaUrl, setMediaUrl] = useState('');

  // New page form state
  const [newPage, setNewPage] = useState<Partial<PageConfig>>({
    route_path: '',
    modelconfig_id: modelconfigId || '',
    app_id: '',
    page_title: '',
    page_description: ''
  });

  // Load existing pages for this model or agent template
  useEffect(() => {
    const idOk = variant === 'agent' ? agentTemplateId : modelconfigId;
    if (isOpen && idOk) {
      loadExistingPages();
      loadApiKeys();
    }
  }, [isOpen, modelconfigId, variant, agentTemplateId]);

  const loadExistingPages = async () => {
    try {
      setLoading(true);
      setError(null);

      let q = supabase.from('pages').select('*').order('created_at', { ascending: false });
      if (variant === 'agent' && agentTemplateId) {
        q = q.eq('agent_template_id', agentTemplateId);
      } else {
        q = q.eq('modelconfig_id', modelconfigId);
      }

      const { data, error: fetchError } = await q;

      if (fetchError) {
        throw fetchError;
      }

      setExistingPages(data || []);
    } catch (err) {
      console.error('Error loading pages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const loadApiKeys = async () => {
    try {
      // Get access record to determine permissions
      const staff = parseAdminStaffSession();

      let query = supabase
        .from('api_keys')
        .select('id, provider, name, is_active')
        .eq('is_active', true)
        .order('provider', { ascending: true })
        .order('name', { ascending: true });

      if (staff && staff.role !== 'superadmin') {
        query = query.eq('hy_user_id', staff.hy_user_id);
      }
      
      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setApiKeys(data || []);
    } catch (err) {
      console.error('Error loading API keys:', err);
      // Don't show error for API keys loading failure, just log it
    }
  };

  const validateRoutePath = (path: string): string | null => {
    if (!path.trim()) {
      return 'Route path is required';
    }

    // Remove leading slash if present for validation, we'll add it back when saving
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    
    if (!cleanPath) {
      return 'Route path cannot be empty';
    }

    // Check for valid URL path characters
    const validPattern = /^[a-zA-Z0-9\-_/]+$/;
    if (!validPattern.test(cleanPath)) {
      return 'Route path can only contain letters, numbers, hyphens, underscores, and forward slashes';
    }

    if (variant === 'model') {
      const existsForOtherModel = existingPages.some(
        (page) => page.route_path === `/${cleanPath}` && page.modelconfig_id !== (modelconfigId || '')
      );
      if (existsForOtherModel) {
        return 'This route path is already used by another model';
      }
    } else if (variant === 'agent' && agentTemplateId) {
      const existsForOtherAgent = existingPages.some(
        (page) => page.route_path === `/${cleanPath}` && page.agent_template_id !== agentTemplateId
      );
      if (existsForOtherAgent) {
        return 'This route path is already used by another agent page';
      }
    }

    return null;
  };

  const handleCreatePage = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (variant === 'model') {
        if (!modelconfigId || modelconfigId === 'new-model' || modelconfigId.length < 10) {
          setError('Please save the model configuration first before creating public pages');
          return;
        }
      } else {
        if (!agentTemplateId || agentTemplateId.length < 10) {
          setError('Save the agent template first before creating public pages');
          return;
        }
      }

      // Validate route path
      const routeError = validateRoutePath(newPage.route_path || '');
      if (routeError) {
        setError(routeError);
        return;
      }

      const staff = parseAdminStaffSession();
      const ownerHy = staff?.hy_user_id?.trim() || null;

      // Ensure route path starts with /
      const cleanRoutePath = newPage.route_path?.startsWith('/') 
        ? newPage.route_path 
        : `/${newPage.route_path}`;

      const pageData =
        variant === 'agent'
          ? {
              route_path: cleanRoutePath,
              modelconfig_id: null,
              agent_template_id: agentTemplateId,
              app_id: newPage.app_id?.trim() || '',
              page_title: newPage.page_title?.trim() || null,
              page_description: newPage.page_description?.trim() || null,
              api_key_id: newPage.api_key_id || null,
              example_media_url: newPage.example_media_url || null,
              example_media_type: newPage.example_media_type || null,
              hy_user_id: ownerHy,
            }
          : {
              route_path: cleanRoutePath,
              modelconfig_id: modelconfigId || '',
              app_id: newPage.app_id?.trim() || '',
              page_title: newPage.page_title?.trim() || null,
              page_description: newPage.page_description?.trim() || null,
              api_key_id: newPage.api_key_id || null,
              example_media_url: newPage.example_media_url || null,
              example_media_type: newPage.example_media_type || null,
              hy_user_id: ownerHy,
            };

      const insertPayload = sanitizeUuidFields(pageData as Record<string, unknown>) as typeof pageData;

      const { error: insertError } = await supabase
        .from('pages')
        .insert(insertPayload)
        .select()
        .single();

      if (insertError) {
        if (insertError.code === '23505') { // Unique constraint violation
          throw new Error('This route path already exists');
        }
        throw insertError;
      }

      // Reset form and reload pages
      setNewPage({ 
        route_path: '', 
        modelconfig_id: modelconfigId || '',
        app_id: '',
        page_title: '', 
        page_description: '',
        api_key_id: '',
        example_media_url: '',
        example_media_type: ''
      });
      setSuccess('Public page created successfully!');
      await loadExistingPages();

    } catch (err) {
      console.error('Error creating page:', err);
      setError(err instanceof Error ? err.message : 'Failed to create page');
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!confirm('Are you sure you want to delete this public page? This cannot be undone.')) {
      return;
    }

    try {
      setDeleting(pageId);
      setError(null);

      const { error: deleteError } = await supabase
        .from('pages')
        .delete()
        .eq('id', pageId);

      if (deleteError) {
        throw deleteError;
      }

      setSuccess('Public page deleted successfully');
      await loadExistingPages();

    } catch (err) {
      console.error('Error deleting page:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete page');
    } finally {
      setDeleting(null);
    }
  };

  const handleUpdatePage = async (pageId: string, updates: Partial<PageConfig>) => {
    try {
      setError(null);

      // If updating route path, validate it
      if (updates.route_path !== undefined) {
        const routeError = validateRoutePath(updates.route_path);
        if (routeError) {
          setError(routeError);
          return;
        }

        // Ensure route path starts with /
        updates.route_path = updates.route_path.startsWith('/') 
          ? updates.route_path 
          : `/${updates.route_path}`;
      }

      const staff = parseAdminStaffSession();
      if (staff?.hy_user_id) {
        updates.hy_user_id = staff.hy_user_id;
      }

      const payload = sanitizeUuidFields(updates as Record<string, unknown>) as Partial<PageConfig>;

      const { error: updateError } = await supabase
        .from('pages')
        .update(payload)
        .eq('id', pageId);

      if (updateError) {
        if (updateError.code === '23505') {
          throw new Error('This route path already exists');
        }
        throw updateError;
      }

      setSuccess('Page updated successfully');
      await loadExistingPages();

    } catch (err) {
      console.error('Error updating page:', err);
      setError(err instanceof Error ? err.message : 'Failed to update page');
    }
  };

  const generateSuggestedPath = () => {
    if (modelName) {
      const suggested = modelName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
      
      setNewPage(prev => ({ ...prev, route_path: suggested }));
    }
  };

  const clearExampleMedia = () => {
    setNewPage(prev => ({
      ...prev,
      example_media_url: '',
      example_media_type: ''
    }));
    setMediaUrl('');
  };

  const handleUrlSubmit = () => {
    if (mediaUrl.trim()) {
      // Determine media type from URL extension
      const url = mediaUrl.trim();
      const extension = url.split('.').pop()?.toLowerCase() || '';
      
      let mediaType: 'image' | 'video' | 'audio' = 'image';
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        mediaType = 'video';
      } else if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
        mediaType = 'audio';
      }
      
      setNewPage(prev => ({
        ...prev,
        example_media_url: url,
        example_media_type: mediaType
      }));
    }
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-base/90 z-modal-content flex items-center justify-center p-4">
      <div className="bg-surface border border-subtle rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-subtle">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-status-warning rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-th-primary">Public Page Settings</h2>
              <p className="text-sm text-th-secondary">
                {variant === 'agent'
                  ? `Public pages for agent: ${modelName}`
                  : `Manage public pages for: ${modelName}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Create New Page */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-th-primary">Create New Public Page</h3>
              <button
                onClick={generateSuggestedPath}
                className="text-sm text-accent hover:text-accent/80 underline"
              >
                {variant === 'agent' ? 'Generate from agent name' : 'Generate from model name'}
              </button>
            </div>

            <div className="space-y-4">
              {/* Route Path */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">
                  Route Path *
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-th-secondary text-sm">/p/</span>
                  <input
                    type="text"
                    value={newPage.route_path || ''}
                    onChange={(e) => setNewPage(prev => ({ ...prev, route_path: e.target.value }))}
                    placeholder="my-awesome-generator"
                    className="flex-1 bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  />
                </div>
                <p className="text-xs text-th-secondary">
                  URL-friendly path (letters, numbers, hyphens, underscores only)
                </p>
              </div>

              {/* Page Title */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">
                  Page Title
                </label>
                <input
                  type="text"
                  value={newPage.page_title || ''}
                  onChange={(e) => setNewPage(prev => ({ ...prev, page_title: e.target.value }))}
                  placeholder="My Awesome AI Generator"
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                />
                <p className="text-xs text-th-secondary">
                  Optional title displayed on the public page
                </p>
              </div>

              {/* Page Description */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">
                  Page Description
                </label>
                <textarea
                  value={newPage.page_description || ''}
                  onChange={(e) => setNewPage(prev => ({ ...prev, page_description: e.target.value }))}
                  placeholder="Generate amazing content with AI using this powerful model"
                  rows={3}
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none resize-none transition-all"
                />
                <p className="text-xs text-th-secondary">
                  Optional description shown below the title
                </p>
              </div>

              {/* App ID */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">
                  App ID *
                </label>
                <input
                  type="text"
                  value={newPage.app_id || ''}
                  onChange={(e) => setNewPage(prev => ({ ...prev, app_id: e.target.value }))}
                  placeholder="4c9b8f8a-d21c-4a8b-a0ca-91c4d6911fb1"
                  className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                  required
                />
                <p className="text-xs text-th-secondary">
                  Hyper App ID for authentication (from your Hyper Developer Console)
                </p>
              </div>
              {/* API Key Selection — not used for agent chat (OpenAI via Edge) */}
              {variant !== 'agent' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-th-primary">
                    API Key for Generation
                  </label>
                  <select
                    value={newPage.api_key_id || ''}
                    onChange={(e) => setNewPage(prev => ({ ...prev, api_key_id: e.target.value }))}
                    className="w-full bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary focus:border-brand focus:outline-none transition-all"
                  >
                    <option value="">No API key (use default)</option>
                    {apiKeys.map((apiKey) => (
                      <option key={apiKey.id} value={apiKey.id}>
                        {apiKey.provider} - {apiKey.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-th-secondary">
                    Choose which API key to use for AI generation on this public page
                  </p>
                </div>
              )}
              {variant === 'agent' && (
                <p className="text-xs text-th-secondary border border-subtle rounded-lg p-3 bg-elevated/50">
                  Agent chat uses OpenAI via the server; FAL API keys on this page are not used for chat.
                </p>
              )}

              {/* Example Media Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-th-primary">
                  Example Media (Optional)
                </label>
                
                {/* URL Input and Preview */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="url"
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg or video.mp4 or audio.mp3"
                      className="flex-1 bg-elevated border border-subtle rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleUrlSubmit}
                      disabled={!mediaUrl.trim()}
                      className="flex items-center space-x-2 px-4 py-3 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Preview</span>
                    </button>
                  </div>

                  {/* URL Media Preview */}
                  {newPage.example_media_url && (
                    <div className="bg-elevated border border-subtle rounded-lg p-4">
                      {newPage.example_media_type === 'image' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-status-success text-sm font-medium">Image Preview</span>
                            <button
                              onClick={clearExampleMedia}
                              className="text-status-error hover:text-status-error/80"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="w-full max-w-md mx-auto">
                            <img
                              src={newPage.example_media_url}
                              alt="URL preview"
                              className="w-full h-auto max-h-64 object-contain rounded-lg border border-subtle"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement?.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="text-center text-status-error p-4"><svg class="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg><p>Failed to load image</p></div>';
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : newPage.example_media_type === 'video' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-status-success text-sm font-medium">Video Preview</span>
                            <button
                              onClick={clearExampleMedia}
                              className="text-status-error hover:text-status-error/80"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="w-full max-w-md mx-auto">
                            <video
                              src={newPage.example_media_url}
                              className="w-full h-auto max-h-64 object-contain rounded-lg border border-subtle"
                              controls
                              preload="metadata"
                              onError={(e) => {
                                const target = e.target as HTMLVideoElement;
                                target.style.display = 'none';
                                const parent = target.parentElement?.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="text-center text-status-error p-4"><svg class="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg><p>Failed to load video</p></div>';
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-status-success text-sm font-medium">Audio Preview</span>
                            <button
                              onClick={clearExampleMedia}
                              className="text-status-error hover:text-status-error/80"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="w-full max-w-md mx-auto text-center">
                            <div className="w-16 h-16 bg-status-warning/20 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Headphones className="w-8 h-8 text-status-warning" />
                            </div>
                            <audio
                              src={newPage.example_media_url}
                              className="w-full bg-elevated rounded-lg"
                              controls
                              preload="metadata"
                              onError={(e) => {
                                const target = e.target as HTMLAudioElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="text-center text-status-error p-4"><svg class="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg><p>Failed to load audio</p></div>';
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-th-secondary">
                  Enter a direct URL to an image, video, or audio file to showcase on the public page
                </p>
              </div>

              {/* Create Button */}
              <button
                onClick={handleCreatePage}
                disabled={!newPage.route_path?.trim() || !newPage.app_id?.trim()}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-status-warning text-black rounded-lg font-medium hover:bg-status-warning/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Create Public Page</span>
              </button>
            </div>
          </div>

          {/* Success/Error Messages */}
          {success && (
            <div className="p-4 bg-status-success/10 border border-status-success/30 rounded-lg">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-status-success" />
                <span className="text-status-success font-medium">{success}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-status-error" />
                <span className="text-status-error">{error}</span>
              </div>
            </div>
          )}

          {/* Existing Pages */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-th-primary">Existing Public Pages</h3>
              {existingPages.length > 0 && (
                <span className="text-sm text-th-secondary bg-elevated px-3 py-1 rounded-full">
                  {existingPages.length} page{existingPages.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <LoadingSpinner size="lg" />
                <p className="text-th-secondary mt-4">Loading pages...</p>
              </div>
            ) : existingPages.length === 0 ? (
              <div className="text-center py-8">
                <Globe className="w-16 h-16 text-th-secondary mx-auto mb-4" />
                <h4 className="text-th-primary font-medium mb-2">No Public Pages</h4>
                <p className="text-th-secondary text-sm">
                  Create your first public page for this model above
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {existingPages.map((page) => (
                  <PageEditCard
                    key={page.id}
                    page={page}
                    onUpdate={handleUpdatePage}
                    onDelete={handleDeletePage}
                    isDeleting={deleting === page.id}
                    apiKeys={apiKeys}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Example Media Gallery Modal */}

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-subtle">
          <div className="text-sm text-th-secondary">
          </div>
        </div>
      </div>
    </div>
  );
};

// Individual page edit card component
interface PageEditCardProps {
  page: PageConfig;
  onUpdate: (pageId: string, updates: Partial<PageConfig>) => void;
  onDelete: (pageId: string) => void;
  isDeleting: boolean;
  apiKeys: ApiKey[];
}

const PageEditCard = ({ page, onUpdate, onDelete, isDeleting, apiKeys }: PageEditCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [editData, setEditData] = useState({
    route_path: page.route_path,
    page_title: page.page_title || '',
    page_description: page.page_description || '',
    app_id: page.app_id || '',
    api_key_id: page.api_key_id || '',
    example_media_url: page.example_media_url || '',
    example_media_type: page.example_media_type || ''
  });

  // Initialize media URL when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setMediaUrl(page.example_media_url || '');
    }
  }, [isEditing, page.example_media_url]);

  const handleSave = () => {
    onUpdate(page.id!, editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      route_path: page.route_path,
      page_title: page.page_title || '',
      page_description: page.page_description || '',
      app_id: page.app_id || '',
      api_key_id: page.api_key_id || '',
      example_media_url: page.example_media_url || '',
      example_media_type: page.example_media_type || ''
    });
    setMediaUrl(page.example_media_url || '');
    setIsEditing(false);
  };

  const handleUrlSubmit = () => {
    if (mediaUrl.trim()) {
      // Determine media type from URL extension
      const url = mediaUrl.trim();
      const extension = url.split('.').pop()?.toLowerCase() || '';
      
      let mediaType: 'image' | 'video' | 'audio' = 'image';
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        mediaType = 'video';
      } else if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
        mediaType = 'audio';
      }
      
      setEditData(prev => ({
        ...prev,
        example_media_url: url,
        example_media_type: mediaType
      }));
    }
  };

  const clearExampleMedia = () => {
    setEditData(prev => ({
      ...prev,
      example_media_url: '',
      example_media_type: ''
    }));
    setMediaUrl('');
  };

  const getFullUrl = () => {
    return `${window.location.origin}/p${page.route_path}`;
  };

  return (
    <div className="bg-elevated border border-subtle rounded-lg p-4">
      {isEditing ? (
        // Edit Mode
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              Route Path
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-th-secondary text-sm">/p/</span>
              <input
                type="text"
                value={editData.route_path.startsWith('/') ? editData.route_path.slice(1) : editData.route_path}
                onChange={(e) => setEditData(prev => ({ 
                  ...prev, 
                  route_path: e.target.value.startsWith('/') ? e.target.value : `/${e.target.value}`
                }))}
                className="flex-1 bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              Page Title
            </label>
            <input
              type="text"
              value={editData.page_title}
              onChange={(e) => setEditData(prev => ({ ...prev, page_title: e.target.value }))}
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              App ID *
            </label>
            <input
              type="text"
              value={editData.app_id || ''}
              onChange={(e) => setEditData(prev => ({ ...prev, app_id: e.target.value }))}
              placeholder="4c9b8f8a-d21c-4a8b-a0ca-91c4d6911fb1"
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm"
            />
            <p className="text-xs text-th-secondary">
              Hyper App ID for authentication (from your Hyper Developer Console)
            </p>
          </div>

          {/* API Key Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              API Key for Generation
            </label>
            <select
              value={editData.api_key_id || ''}
              onChange={(e) => setEditData(prev => ({ ...prev, api_key_id: e.target.value }))}
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm"
            >
              <option value="">No API key (use default)</option>
              {apiKeys.map((apiKey) => (
                <option key={apiKey.id} value={apiKey.id}>
                  {apiKey.provider} - {apiKey.name}
                </option>
              ))}
            </select>
          </div>

          {/* Page Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              Page Description
            </label>
            <textarea
              value={editData.page_description}
              onChange={(e) => setEditData(prev => ({ ...prev, page_description: e.target.value }))}
              rows={2}
              className="w-full bg-surface border border-subtle rounded px-3 py-2 text-th-primary focus:border-brand focus:outline-none text-sm resize-none"
            />
          </div>

          {/* Example Media URL */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-th-primary">
              Example Media URL (Optional)
            </label>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <input
                  type="url"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg or video.mp4 or audio.mp3"
                  className="flex-1 bg-surface border border-subtle rounded px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={handleUrlSubmit}
                  disabled={!mediaUrl.trim()}
                  className="flex items-center space-x-2 px-3 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
                >
                  <Eye className="w-3 h-3" />
                  <span>Preview</span>
                </button>
              </div>

              {/* URL Media Preview */}
              {editData.example_media_url && (
                <div className="bg-surface border border-subtle rounded-lg p-3">
                  {editData.example_media_type === 'image' ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-status-success text-sm font-medium">Image Preview</span>
                        <button
                          type="button"
                          onClick={clearExampleMedia}
                          className="text-status-error hover:text-status-error/80"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="w-full max-w-sm">
                        <img
                          src={editData.example_media_url}
                          alt="URL preview"
                          className="w-full h-auto max-h-32 object-contain rounded border border-subtle"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement?.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="text-center text-status-error p-2"><p class="text-xs">Failed to load image</p></div>';
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : editData.example_media_type === 'video' ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-status-success text-sm font-medium">Video Preview</span>
                        <button
                          type="button"
                          onClick={clearExampleMedia}
                          className="text-status-error hover:text-status-error/80"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="w-full max-w-sm">
                        <video
                          src={editData.example_media_url}
                          className="w-full h-auto max-h-32 object-contain rounded border border-subtle"
                          controls
                          preload="metadata"
                          onError={(e) => {
                            const target = e.target as HTMLVideoElement;
                            target.style.display = 'none';
                            const parent = target.parentElement?.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="text-center text-status-error p-2"><p class="text-xs">Failed to load video</p></div>';
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : editData.example_media_type === 'audio' ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-status-success text-sm font-medium">Audio Preview</span>
                        <button
                          type="button"
                          onClick={clearExampleMedia}
                          className="text-status-error hover:text-status-error/80"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="w-full max-w-sm text-center">
                        <div className="w-12 h-12 bg-status-warning/20 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Headphones className="w-6 h-6 text-status-warning" />
                        </div>
                        <audio
                          src={editData.example_media_url}
                          className="w-full bg-elevated rounded text-sm"
                          controls
                          preload="metadata"
                          onError={(e) => {
                            const target = e.target as HTMLAudioElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="text-center text-status-error p-2"><p class="text-xs">Failed to load audio</p></div>';
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
              
              <p className="text-xs text-th-secondary">
                Enter a direct URL to an image, video, or audio file
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleSave}
              className="flex items-center space-x-1 px-3 py-2 bg-status-success text-white rounded-lg text-sm font-medium hover:bg-status-success/90 transition-all"
            >
              <Save className="w-3 h-3" />
              <span>Save</span>
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-2 bg-elevated border border-subtle text-th-secondary rounded-lg text-sm font-medium hover:text-th-primary transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // View Mode
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <Link className="w-4 h-4 text-status-warning" />
                <a
                  href={getFullUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-status-warning hover:text-status-warning/80 font-medium text-sm underline"
                >
                  {getFullUrl()}
                </a>
              </div>
              
              {page.page_title && (
                <h4 className="text-th-primary font-medium mb-1">
                  {page.page_title}
                </h4>
              )}
              
              {page.page_description && (
                <p className="text-th-secondary text-sm line-clamp-2">
                  {page.page_description}
                </p>
              )}
              
              {page.example_media_url && (
                <div className="mt-2 p-2 bg-status-warning/10 border border-status-warning/20 rounded">
                  <p className="text-status-warning text-xs font-medium">Example Media:</p>
                  <p className="text-th-secondary text-xs truncate">
                    {page.example_media_type} • {page.example_media_url}
                  </p>
                </div>
              )}
              
              {/* API Key Info */}
              {(page as any).api_keys && (page as any).api_keys.provider && (
                <div className="mt-2 p-2 bg-brand/10 border border-brand/30 rounded">
                  <div className="flex items-center space-x-2">
                    <Key className="w-3 h-3 text-brand" />
                    <p className="text-brand text-xs font-medium">
                      API Key: {(page as any).api_keys.provider} - {(page as any).api_keys.name} (ID: {(page as any).api_keys.id})
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-4 mt-2 text-xs text-th-secondary">
                <span>App ID: {page.app_id}</span>
                <span>Created: {new Date(page.created_at!).toLocaleDateString()}</span>
                <span>Updated: {new Date(page.updated_at!).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-2 bg-accent/20 border border-accent/30 text-accent hover:bg-accent/30 rounded-lg text-sm font-medium transition-all"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(page.id!)}
                disabled={isDeleting}
                className="flex items-center space-x-1 px-3 py-2 bg-status-error/20 border border-status-error/30 text-status-error hover:bg-status-error/30 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
              >
                {isDeleting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Trash2 className="w-3 h-3" />
                )}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};